import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import PageHero from '../../components/PageHero';
import ProductCard from '../../components/ProductCard';
import MagneticButton from '../../components/MagneticButton';
import { RevealGroup, RevealItem } from '../../components/Reveal';
import '../css/AllProducts.css';

const AllProducts = () => {
  const [selectedCategory, setSelectedCategory] = useState('All Products');
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('Featured');

  const products = [
    {
      id: 1,
      name: 'Professional Paint Brush Set',
      category: 'Paint Tools',
      image: '/images/all-products/product-1.png',
      colors: ['#1e3a5f', '#757575', '#d4d4d4'],
      features: ['Premium Bristles', 'Ergonomic Grip', 'Storage Case', 'Lifetime Warranty']
    },
    {
      id: 2,
      name: 'Premium Roller & Sleeve Kit',
      category: 'Paint Tools',
      image: '/images/all-products/product-2.png',
      colors: ['#1e3a5f', '#757575'],
      features: ['Multiple Sizes', 'Easy Clean', 'Anti-Drip Design', 'Durable Frame']
    },
    {
      id: 3,
      name: 'Precision Finishing Brush',
      category: 'Paint Tools',
      image: '/images/all-products/product-3.png',
      colors: ['#1e3a5f', '#757575', '#d4d4d4'],
      features: ['Fine Bristles', 'Smooth Application', 'Decorative Work', 'Comfortable Grip']
    },
    {
      id: 4,
      name: 'Professional Putty Knife Set',
      category: 'Surface Prep',
      image: '/images/all-products/product-4.png',
      colors: ['#1e3a5f', '#757575'],
      features: ['Stainless Steel', 'Flexible Blade', 'Multiple Options', 'Rust Resistant']
    },
    {
      id: 5,
      name: 'Notched Trowel Collection',
      category: 'Surface Prep',
      image: '/images/all-products/product-5.png',
      colors: ['#1e3a5f', '#757575'],
      features: ['Multiple Patterns', 'Ergonomic Grip', 'Durable Build', 'Professional Grade']
    },
    {
      id: 6,
      name: 'Facade Scraper Tool',
      category: 'Surface Prep',
      image: '/images/all-products/product-6.png',
      colors: ['#1e3a5f', '#757575'],
      features: ['Wide Blade', 'Strong Handle', 'Surface Prep', 'Long-lasting']
    },
    {
      id: 7,
      name: 'Sanding Sponge Assortment',
      category: 'Sanding',
      image: '/images/all-products/product-7.png',
      colors: ['#1e3a5f', '#757575'],
      features: ['Multiple Grits', 'Reusable', 'Comfortable', 'Washable']
    },
    {
      id: 8,
      name: 'Abrasive Steel Wool Pack',
      category: 'Sanding',
      image: '/images/all-products/product-8.png',
      colors: ['#1e3a5f', '#757575'],
      features: ['Various Grades', 'Rust Free', 'Fine Finishing', 'Bulk Pack']
    },
    {
      id: 9,
      name: 'Drywall Sanding Screen Set',
      category: 'Sanding',
      image: '/images/all-products/product-9.png',
      colors: ['#1e3a5f', '#757575'],
      features: ['Mesh Design', 'Dust Reduction', 'Multiple Grits', 'Long Life']
    }
  ];

  const categories = ['All Products', 'Paint Tools', 'Surface Prep', 'Sanding', 'Accessories'];
  const sortOptions = ['Featured', 'Name: A to Z', 'Name: Z to A', 'Category'];

  const filteredProducts = useMemo(() => {
    const base = products.filter(product => {
      if (selectedCategory === 'All Products') return true;
      return product.category === selectedCategory;
    });

    const sorted = [...base];
    if (sortBy === 'Name: A to Z') sorted.sort((a, b) => a.name.localeCompare(b.name));
    if (sortBy === 'Name: Z to A') sorted.sort((a, b) => b.name.localeCompare(a.name));
    if (sortBy === 'Category') sorted.sort((a, b) => a.category.localeCompare(b.category));
    return sorted;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, sortBy]);

  return (
    <div className="all-products">
      <PageHero
        eyebrow="Catalogue"
        title="All Products"
        subtitle="Browse our complete range of professional painting and surface preparation tools."
      />

      <div className="container">
        <div className="products-toolbar">
          <div className="category-tabs">
            {categories.map(category => (
              <button
                key={category}
                className={`category-tab ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
          <div className="toolbar-right">
            <span className="sort-label">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              {sortOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="products-subheader">
          <span className="products-count">{filteredProducts.length} products found</span>
          <div className="view-toggle">
            <button
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              aria-label="Grid view"
            >
              <GridViewOutlinedIcon />
            </button>
            <button
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              aria-label="List view"
            >
              <ViewListOutlinedIcon />
            </button>
          </div>
        </div>

        <RevealGroup className={`products-grid ${viewMode}`}>
          {filteredProducts.map(product => (
            <RevealItem key={product.id}>
              <ProductCard
                to={`/product/${product.id}`}
                image={product.image}
                title={product.name}
                subtitle={product.category}
                colors={product.colors}
                features={product.features}
              />
            </RevealItem>
          ))}
        </RevealGroup>

        <div className="help-section glass-card">
          <div className="help-content">
            <h2>Need Help Finding Products?</h2>
            <p>Our expert team can help you select the right products for your market and provide custom quotations for bulk orders.</p>
          </div>
          <div className="help-actions">
            <MagneticButton to="/contact" className="btn-primary">Contact Expert Team</MagneticButton>
            <Link to="/products" className="btn-ghost">Download Catalog</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllProducts;
